/**
 * Apps & Data Tables API client (ADR-0010).
 *
 * Mirrors `backend/src/hub/routes/apps.py`. Covers the app /
 * table / column / unique-set metadata CRUD plus the structured row
 * Data API (query / create / update / delete / upsert). Physical
 * schema names are never exposed — everything is id-based and the
 * backend resolves ids to the real `tray_data_*` tables.
 *
 * Used by the Apps section (AppsPage → app detail → table designer +
 * data grid). RBAC: every route is gated on the tag-scoped `apps`
 * resource (`apps:read` / `apps:write`).
 */
import { api } from "./api";
import type { PageAst, PageComponentSpec, PageQuery, StateValueType } from "./pageTypes";

export type { PageAst, PageComponentSpec, PageNode } from "./pageTypes";

// ---------------------------------------------------------------------------
// Types — mirror the Pydantic response/request models 1:1
// ---------------------------------------------------------------------------

/** App data environment (ADR-0010 P5.5). ``dev`` always exists; ``prod`` is
 *  provisioned on the first promote. */
export type AppEnv = "dev" | "prod";

/** Per-app portal brand & layout (ADR-0010 P7 B+C). Mirrors backend
 *  `PortalConfig`; all optional, `{}`-equivalent = the "standard" layout.
 *  `theme` is a free-form slot→colour map; the available slots are defined
 *  per-layout in `lib/portalLayouts`. */
/** One configured portal-nav entry (improvement #1). Stored in
 *  `portal_config.nav`; resolved by the public `…/nav` endpoint. */
export interface PortalNavConfigItem {
  kind: "page" | "link" | "group";
  /** kind=page: target page slug. */
  page?: string | null;
  /** kind=link: external href. */
  url?: string | null;
  /** Display label (page title override / link text / group header). */
  label?: string | null;
  /** Optional lucide icon name (see lib/portalNavIcons). */
  icon?: string | null;
  /** kind=group: child page/link items (one level deep). */
  children?: PortalNavConfigItem[];
}

export interface PortalConfig {
  layout: string;
  theme: Record<string, string>;
  logo_url?: string | null;
  login: { title?: string | null; subtitle?: string | null };
  show_nav: boolean;
  /** Default page content width: "contained" | "full" (S8b). */
  content_width?: string;
  /** Configured navigation; empty/absent = automatic page list (improvement #1). */
  nav?: PortalNavConfigItem[];
  /** ADR-0049 — the web app (portal) can be turned OFF so an app is a pure
   * chat / voice / form app with no web pages. */
  web_enabled?: boolean;
  /** ADR-0049 — the app's default front door ("web" | "chat" | "voice" |
   * "form:<id>"); null/absent = auto (web if enabled, else first standalone). */
  home_interface?: string | null;
}

export interface AppSummary {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  enabled: boolean;
  tags: string[];
  tables_count: number;
  /** NULL until the first promote-to-prod; then the most recent timestamp. */
  prod_promoted_at: string | null;
  portal_config: PortalConfig;
  created_by: string;
  created_at: string;
  updated_at: string | null;
}

export interface PromoteResult {
  app: AppSummary;
  tables_provisioned: number;
  /** False on the first promote, True on a re-promote (which reset prod data). */
  was_reprovision: boolean;
}

/** Append ``?env=prod`` only when targeting a non-default env (keeps dev URLs
 *  clean + backwards-compatible). */
function envQuery(env?: AppEnv): string {
  return env && env !== "dev" ? `?env=${env}` : "";
}

export interface ColumnOut {
  id: string;
  name: string;
  kind: string;
  data_type: string | null;
  required: boolean;
  config: Record<string, unknown>;
  position: number;
}

/** A relation-picker option: a target row's id + its display label. */
export interface RelationOption {
  id: string;
  label: string;
}

export interface UniqueSetOut {
  id: string;
  name: string;
  column_ids: string[];
}

export interface TableSummary {
  id: string;
  app_id: string;
  name: string;
  description: string | null;
  columns_count: number;
  created_by: string;
  created_at: string;
  updated_at: string | null;
}

export interface TableDetail extends TableSummary {
  columns: ColumnOut[];
  unique_sets: UniqueSetOut[];
}

/** One declared app-wide `$global` state entry (WS-4a). The runtime bag is the
 *  client `portalStore` (survives navigation); this is the authoring declaration
 *  (name + optional type + initial seed). Mirrors the backend
 *  `AppGlobalStateEntry`. The page-scoped twin is `PageVariable` (the `$page`
 *  scope), which additionally carries an `id`. */
export interface AppGlobalStateEntry {
  name: string;
  type?: StateValueType | null;
  initial?: unknown;
}

export interface AppDetail extends AppSummary {
  tables: TableSummary[];
  /** Default role for admin assignment + auto-enroll; NULL = none (ADR-0016). */
  default_role_id?: string | null;
  /** Auto-grant this app (with default_role_id) on account activation. */
  auto_enroll?: boolean;
  /** ER schema-diagram saved node positions: {"positions": {id: {x,y}}}.
   *  `{}` / absent = auto-layout. */
  er_layout?: ErLayout;
  /** Declared app-wide `$global` state bag (WS-4a). `[]` / absent = none. */
  global_state?: AppGlobalStateEntry[];
  /** External JS libraries (ADR-0046) — runtime-CDN pointers the page builder
   *  loads so a binding / JS Object can use their globals. `[]` / absent = none. */
  js_libraries?: JsLibraryOut[];
}

/** Saved ER diagram layout — node positions keyed by table / dataset id. */
export interface ErLayout {
  positions?: Record<string, { x: number; y: number }>;
}

export interface DataTypeInfo {
  id: string;
  sql: string;
}

// Forms (ADR-0010 D11) ------------------------------------------------------

/** One field in a form's config — maps to a Data Table column. */
export interface FormField {
  column: string; // column id
  label?: string | null;
  help?: string | null;
  required?: boolean | null;
  widget?: string | null;
  default?: unknown;
  /** Conditional visibility: `{siblingColumnName: value | [values]}`.
   *  The field shows only when every sibling equals the wanted value(s). */
  show_when?: Record<string, unknown> | null;
  /** Form pro A — the layout section this field belongs to (section id), or
   *  null/absent for the implicit default section. */
  section?: string | null;
  /** Form pro A — grid columns this field spans (1 or 2). */
  col_span?: number | null;
  /** Form pro B — per-field validation rules. */
  validation?: import("@/lib/formValidation").FieldValidation | null;
}

/** Form pro A — a layout section: a heading + a 1/2-column grid grouping a run
 *  of fields. */
export interface FormSection {
  id: string;
  title?: string | null;
  description?: string | null;
  /** Grid columns for this section (1 or 2). */
  columns?: number | null;
}

export interface FormConfig {
  fields?: FormField[];
  submit_message?: string | null;
  /** Form pro A — explicit layout sections (absent → a single column stack). */
  sections?: FormSection[];
  /** Form pro A — grid columns for the implicit default section (1 or 2). */
  columns?: number | null;
}

export interface FormOut {
  id: string;
  app_id: string;
  table_id: string;
  name: string;
  slug: string;
  description: string | null;
  access: string;
  enabled: boolean;
  config: FormConfig;
  created_by: string;
  created_at: string;
  updated_at: string | null;
}

export interface FormCreate {
  name: string;
  slug: string;
  table_id: string;
  description?: string | null;
  access?: string;
  config?: FormConfig;
}

export interface FormUpdate {
  name?: string;
  description?: string | null;
  access?: string;
  enabled?: boolean;
  config?: FormConfig;
}

// Pages (P7)
export interface PageOut {
  id: string;
  app_id: string;
  slug: string;
  title: string;
  is_home: boolean;
  /** Menu page (sidebar) vs detail page (hidden from nav). */
  show_in_nav: boolean;
  access: string;
  enabled: boolean;
  /** Overlay page (ADR-0023): authored like any page but opened as a
   *  modal/drawer by an `open_overlay` action, not a navigable route. */
  overlay: boolean;
  /** Default presentation of an overlay page (an `open_overlay` may override). */
  overlay_mode: "modal" | "drawer";
  ast: PageAst;
  visible_to_roles: string[];
  created_by: string;
  created_at: string;
  updated_at: string | null;
}

export interface PageCreate {
  slug: string;
  title: string;
  access?: string;
  is_home?: boolean;
  show_in_nav?: boolean;
  overlay?: boolean;
  overlay_mode?: "modal" | "drawer";
  ast?: PageAst;
  visible_to_roles?: string[];
}

export interface PageUpdate {
  title?: string;
  slug?: string;
  access?: string;
  is_home?: boolean;
  show_in_nav?: boolean;
  overlay?: boolean;
  overlay_mode?: "modal" | "drawer";
  enabled?: boolean;
  ast?: PageAst;
  visible_to_roles?: string[];
}

export interface FormSubmitResult {
  row: Row;
  submit_message: string | null;
}

export interface AttachmentOut {
  ref: string;
  filename: string;
  size: number;
  content_type: string | null;
}

/** The value stored in a `file`-type column (S2): a descriptor pointing at a
 *  BlobStorage object. Same shape as AttachmentOut. */
export type FileValue = AttachmentOut;

// --- App Queries (ADR-0047) — the app-level server primitive --------------

/** App-level Query source kind. `js` is labelled **Compute** (read-only) in the
 *  UI; `logic` is **Logic** (server business logic that writes + validates). */
export type AppQuerySourceKind = "table" | "dataset" | "http" | "js" | "logic" | "static";
export type AppQueryTrigger = "manual" | "schedule";

/** A declared INPUT parameter of a Query (ADR-0054) — validated server-side → 422. */
export type QueryParamType = "string" | "number" | "boolean" | "object" | "array";
export type QueryParamLocation = "query" | "path" | "body";
export interface QueryParam {
  name: string;
  type: QueryParamType;
  required: boolean;
  location: QueryParamLocation;
  description?: string | null;
}

export interface AppQueryOut {
  id: string;
  app_id: string;
  key: string;
  name: string;
  description: string | null;
  source_kind: AppQuerySourceKind;
  trigger: AppQueryTrigger;
  /** ResourceSpec-shaped source config (sans identity/kind/trigger). For `logic`/
   *  `js` the body is in `spec.code`; `static` in `spec.value`; table in `spec.table_id`. */
  spec: Record<string, unknown>;
  /** Declared input contract (ADR-0054) — validated server-side on run → 422. */
  input_schema: QueryParam[];
  /** TypeScript source of a js/logic body (ADR-0054 S2). The transpiled JS runs from
   *  `spec.code`; null = a plain-JS body (never authored in TS). */
  code_ts: string | null;
  /** Declared output shape (ADR-0054 S2) — a JSON-schema object, or null. */
  output_schema: Record<string, unknown> | null;
  schedule_cron: string | null;
  /** Ownership/scope axis (ADR-0048). null = an app-level Query (created in this
   *  manager). When set, the Query is OWNED BY that page (extracted by the
   *  convergence) and the editor nests it under the page. */
  page_id: string | null;
  enabled: boolean;
}

/** Minimal shape of the generated OpenAPI doc the API-reference page renders. */
export interface OpenApiParam {
  name: string;
  in: "path" | "query";
  required?: boolean;
  description?: string;
  schema?: { type?: string };
}
export interface OpenApiOperation {
  operationId?: string;
  summary?: string;
  description?: string;
  /** The resource-grouping tag (ADR-0051) — one section per tag in the docs. */
  tags?: string[];
  parameters?: OpenApiParam[];
  requestBody?: {
    required?: boolean;
    content?: { "application/json"?: { schema?: { properties?: Record<string, { type?: string; description?: string }>; required?: string[] } } };
  };
}
export interface OpenApiDoc {
  openapi: string;
  info: { title: string; version: string };
  /** Distinct resources in declaration order (drives the docs section order). */
  tags?: { name: string }[];
  paths: Record<string, Record<string, OpenApiOperation>>;
}

export interface AppQueryUpsert {
  key: string;
  name: string;
  description?: string | null;
  source_kind: AppQuerySourceKind;
  trigger?: AppQueryTrigger;
  spec?: Record<string, unknown>;
  /** Declared input contract (ADR-0054). Omitted ⇒ no declared inputs. */
  input_schema?: QueryParam[];
  /** TypeScript source of a js/logic body (ADR-0054 S2). */
  code_ts?: string | null;
  /** Declared output shape (ADR-0054 S2), extracted from the TS `Output` type. */
  output_schema?: Record<string, unknown> | null;
  schedule_cron?: string | null;
  enabled?: boolean;
}

// Roles & members (ADR-0010 D7/D8 — app RBAC usage plane) -------------------

export type PrincipalType = "tray_user" | "tray_group" | "app_user";

/**
 * The STORED per-role assistant scope deltas (ADR-0028 N1, inherit revision).
 * The assistant inherits the role's RLS-readable tables/projections by default;
 * `off_*` are operator EXCLUSIONS, and `dataset_ids` is the explicit catalog-
 * dataset allow-list (no per-role RLS to inherit there). `{}` = inherit
 * everything readable, no catalog datasets.
 */
/** Filter operators the per-role dataset row filter may use — the Ask query
 *  engine's set (NOT the table-RLS `PolicyOp`): presence ops are `is_null` /
 *  `is_not_null`, and "not equal" is `ne`. */
export type DatasetFilterOp = "eq" | "ne" | "contains" | "is_null" | "is_not_null";

export interface DatasetFilterCondition {
  field: string;
  op: DatasetFilterOp;
  /** A literal OR a logical key `$user.<key>`; omitted for presence ops. */
  value?: string;
}

export interface DatasetRowFilter {
  match: "all" | "any";
  conditions: DatasetFilterCondition[];
}

/** Per-(role, EXTERNAL dataset) rows+fields refinement (ADR-0028 N4), stored in
 *  `assistant_scope.dataset_grants[<datasetId>]`. `hidden` = field NAMES the role
 *  may NOT see (stripped from schema + rows); `row_filter` slices the rows (a
 *  value may be `$user.<key>` — a logical key bound from the member's profile). */
export interface DatasetGrant {
  hidden?: string[];
  row_filter?: DatasetRowFilter | null;
}

export interface AssistantScope {
  off_table_ids?: string[];
  off_projection_ids?: string[];
  dataset_ids?: string[];
  /** N4 — per-granted-dataset rows+fields refinement (keyed by dataset id). */
  dataset_grants?: Record<string, DatasetGrant>;
  /** ADR-0035 RAG — the Knowledge Bases this role's assistant may search (allow-list). */
  kb_ids?: string[];
  /** ADR-0060 S5 — the Report Types this role may RENDER (allow-list ⊆ linked). */
  report_type_ids?: string[];
}

/** The RESOLVED scope the assistant currently reads for a role (read-only,
 *  derived server-side from the role's RLS-readable set minus the exclusions). */
export interface EffectiveAssistantScope {
  app_table_ids?: string[];
  projection_ids?: string[];
  dataset_ids?: string[];
  /** The resolved (stored) dataset refinements — echoed for the UI. */
  dataset_grants?: Record<string, DatasetGrant>;
  /** ADR-0035 RAG — the resolved KB allow-list (echoed for the matrix). */
  kb_ids?: string[];
  /** ADR-0060 S5 — the resolved report allow-list (echoed for the matrix). */
  report_type_ids?: string[];
}

/** ADR-0059 — a catalog Dataset or Knowledge Base LINKED to an app (Core areas).
 *  `published` is set only for Knowledge Bases. */
export interface LinkedResource {
  id: string;
  name: string;
  description: string | null;
  published?: boolean | null;
}

export interface RoleOut {
  id: string;
  app_id: string;
  name: string;
  slug: string;
  description: string | null;
  is_builtin: boolean;
  /** App-admin flag (ADR-0052 S4b): members of a `can_admin` role may provision
   *  app-scoped service accounts for this app from the portal. NOT a data grant. */
  can_admin: boolean;
  /** ADR-0028 N1 — stored exclusion deltas + dataset allow-list. */
  assistant_scope: AssistantScope;
  /** The resolved set the assistant reads (drives the access matrix). */
  effective_assistant_scope: EffectiveAssistantScope;
  created_by: string;
  created_at: string;
  updated_at: string | null;
}

export interface RoleCreate {
  name: string;
  slug: string;
  description?: string | null;
}

export interface RoleUpdate {
  name?: string;
  description?: string | null;
  can_admin?: boolean;
  assistant_scope?: AssistantScope;
}

export interface MemberOut {
  id: string;
  app_id: string;
  principal_type: PrincipalType;
  principal_id: string;
  /** Friendly principal (app_user email / username / group name), resolved
   *  server-side; `null` if the principal no longer exists. */
  principal_label: string | null;
  role_id: string;
  /** Per-app PROFILE attribute values (`member_attrs`) — only for an
   *  `app_user` principal; `{}` otherwise. Set via `updateMemberAttrs`. */
  attrs: Record<string, unknown>;
  created_by: string;
  created_at: string;
}

export interface MemberCreate {
  principal_type: PrincipalType;
  principal_id: string;
  role_id: string;
}

// Per-app USER PROFILE (access-model) — the operator-declared member-attribute
// schema. A LOCKED field (the default) may back an RLS `$user.<key>` row filter;
// an editable one is a self-serve preference.
export type ProfileFieldDataType = "text" | "number" | "select";

export interface ProfileField {
  id: string;
  app_id: string;
  key: string;
  label: string;
  data_type: ProfileFieldDataType;
  options: string[] | null;
  /** DEFAULTS FALSE (locked = operator-only / RLS-bearing). */
  user_editable: boolean;
  position: number;
}

export interface ProfileFieldCreate {
  key: string;
  label: string;
  data_type?: ProfileFieldDataType;
  options?: string[] | null;
  user_editable?: boolean;
  position?: number;
}

export interface ProfileFieldUpdate {
  label?: string;
  data_type?: ProfileFieldDataType;
  options?: string[] | null;
  user_editable?: boolean;
  position?: number;
}

// Row-level security policies (ADR-0010 D8/P6/P7) — one per (table, role).
// `mode`: full (all rows) | none (no rows) | filtered (predicate). The
// predicate is a CLOSED DSL (NOT SQL): {column, op:"eq", value:"$me"|literal}.
// `column` is a system column name (id/created_at/updated_at/created_by/
// updated_by) OR a user column's id (UUID). `$me` binds to the portal
// principal (`app_user:<id>`). No policy on a table = all rows (allow-all).
export type PolicyMode = "full" | "none" | "filtered";

/** Closed predicate-operator set (mirrors `hub.apps.rls.SUPPORTED_OPS`). */
export type PolicyOp = "eq" | "neq" | "contains" | "is_empty" | "is_not_empty";

export interface PolicyPredicate {
  column: string;
  op: PolicyOp;
  value?: string; // "$me" | literal — omitted for presence-ops (is_empty/is_not_empty)
}

export interface PolicyOut {
  id: string;
  table_id: string;
  role_id: string;
  // SELECT (read) policy.
  mode: PolicyMode;
  predicate: PolicyPredicate | null;
  // Per-operation WRITE policy (S3). null = the backend default: insert→full,
  // update→inherit the read mode/predicate, delete→none.
  insert_mode: "full" | "none" | null;
  update_mode: PolicyMode | null;
  update_predicate: PolicyPredicate | null;
  delete_mode: PolicyMode | null;
  delete_predicate: PolicyPredicate | null;
  // Field-level security (D18): { "<col name|id>": "hidden" | "read_only" }.
  column_grants?: Record<string, "hidden" | "read_only"> | null;
}

export interface PolicyUpsert {
  role_id: string;
  mode: PolicyMode;
  predicate?: PolicyPredicate | null;
  // Write ops (S3). Omit to use the backend default for that op.
  insert_mode?: "full" | "none" | null;
  update_mode?: PolicyMode | null;
  update_predicate?: PolicyPredicate | null;
  delete_mode?: PolicyMode | null;
  delete_predicate?: PolicyPredicate | null;
  // Field-level security (D18). Omit/empty = no column restrictions.
  column_grants?: Record<string, "hidden" | "read_only"> | null;
}

/** A saved Grid view's config (ADR-0010 D5). All keys optional; an empty
 *  `columns` means "show all". `sort`/`filters` are stored for forward
 *  compat — the grid applies `columns` + `page_size` today. */
export interface ViewConfig {
  columns?: string[];
  sort?: RowSort[];
  filters?: RowFilter[];
  page_size?: number;
}

export interface ViewOut {
  id: string;
  table_id: string;
  name: string;
  view_type: string;
  config: ViewConfig;
  position: number;
  created_by: string;
  created_at: string;
  updated_at: string | null;
}

export interface ViewCreate {
  name: string;
  view_type?: string;
  config?: ViewConfig;
  position?: number;
}

export interface ViewUpdate {
  name?: string;
  config?: ViewConfig;
  position?: number;
}

export interface AppCreate {
  name: string;
  slug: string;
  description?: string | null;
  tags?: string[];
}

export interface AppUpdate {
  name?: string;
  slug?: string;
  description?: string | null;
  enabled?: boolean;
  tags?: string[];
  default_role_id?: string | null;
  auto_enroll?: boolean;
  /** Whole-object replace of the portal brand & layout config. */
  portal_config?: PortalConfig;
  /** Whole-LIST replace of the declared `$global` state bag (WS-4a); omit to
   *  leave unchanged, `[]` to clear. */
  global_state?: AppGlobalStateEntry[];
}

export interface ColumnIn {
  name: string;
  data_type: string;
  required?: boolean;
  kind?: string;
  config?: Record<string, unknown>;
}

export interface TableCreate {
  name: string;
  description?: string | null;
  columns?: ColumnIn[];
}

export interface TableUpdate {
  name?: string;
  description?: string | null;
}

export interface UniqueSetIn {
  name: string;
  column_ids: string[];
}

// Row Data API ---------------------------------------------------------------

export interface RowFilter {
  column: string;
  op: string;
  value?: unknown;
}

export interface RowSort {
  column: string;
  descending?: boolean;
}

export interface RowQuery {
  filters?: RowFilter[];
  sort?: RowSort[];
  page?: number;
  page_size?: number;
}

export interface RowsPage {
  rows: Array<Record<string, unknown>>;
  total: number;
  page: number;
  page_size: number;
}

export type Row = Record<string, unknown>;

/** One aggregation metric for the server-side aggregate endpoint. */
export interface AggMetric {
  op: string; // count | sum | avg | min | max
  column?: string; // omit for count(*)
  alias?: string;
}

export interface AggregateBody {
  metrics: AggMetric[];
  group_by?: string[];
  filters?: Array<Record<string, unknown>>;
}

export interface AggregateResult {
  groups: Array<Record<string, unknown>>;
}

// ---------------------------------------------------------------------------
// Datasets (ADR-0019) — the apps READ model: a saved query (base table +
// relation joins + projected/expression columns + filters/sort) compiled to a
// LEFT-JOIN SELECT server-side. Surfaced as a virtual, read-only table.
// ---------------------------------------------------------------------------

/** A projected column: `alias.column` (`alias` is `base` or a join alias). */
export interface DatasetColumnSource {
  alias: string;
  column: string;
}

/** A relation-based join: from `from` (default `base`), follow the declared
 *  relation column `via` to its target table, exposed under `alias`. */
export interface DatasetJoin {
  alias: string;
  via: string;
  from?: string;
}

/** An output column: a projection (`source`) OR a derived `expression` (raw JS
 *  over the joined row, shared formulas.js). Exactly one of the two. */
export interface DatasetColumn {
  key: string;
  label?: string | null;
  source?: DatasetColumnSource | null;
  expression?: string | null;
  result_type?: string | null;
}

/** A predicate over a dataset column `key`; `value` is a literal or a runtime
 *  `{ $param: name }` bound at query time. Same SQL-predicate shape as the grid. */
export interface DatasetFilter {
  column: string;
  op: string;
  value?: unknown;
}

export interface DatasetSort {
  column: string;
  dir?: "asc" | "desc";
}

/** The full read-query spec stored in `datasets.config` (scope='app'). */
export interface DatasetSpec {
  base_table: string;
  joins: DatasetJoin[];
  columns: DatasetColumn[];
  filters: DatasetFilter[];
  group_by?: string[];
  aggregates?: unknown[];
  sort: DatasetSort[];
}

/** ADR-0063 FE-3: a projection's stored `config` is EITHER a legacy `DatasetSpec`
 *  OR the unified app-flavour `config.model` (base_table + `select` + `order_by` …);
 *  the backend `spec_of` dual-reads both, so the API surface accepts either. */
export type DatasetConfig = DatasetSpec | Record<string, unknown>;

export interface DatasetOut {
  id: string;
  app_id: string;
  base_table_id: string;
  name: string;
  slug: string;
  description: string | null;
  config: DatasetConfig;
  created_by: string;
  created_at: string;
  updated_at: string | null;
}

export interface DatasetCreate {
  name: string;
  config: DatasetConfig;
  slug?: string;
  description?: string | null;
}

export interface DatasetUpdate {
  name?: string;
  config?: DatasetConfig;
  description?: string | null;
}

// -- Custom-component library (ADR-0022 · open registries #2) ---------------
/** Authoring mode (ADR-0046): HTML/CSS/JS or a React component. */
export type CustomComponentMode = "html" | "react";

export interface CustomComponentOut {
  id: string;
  app_id: string;
  name: string;
  description: string | null;
  code: string;
  mode: CustomComponentMode;
  created_by: string;
  created_at: string;
  updated_at: string | null;
}

export interface CustomComponentCreate {
  name: string;
  code: string;
  description?: string | null;
  mode?: CustomComponentMode;
}

export interface CustomComponentUpdate {
  name?: string;
  code?: string;
  description?: string | null;
  mode?: CustomComponentMode;
}

// -- External JS libraries (ADR-0046) — per-app, runtime-CDN pointers --------
export type JsLibraryFormat = "umd" | "esm";

/** A stored library pointer. `global` is the window global (UMD) / import
 *  specifier (ESM); `url` is the pinned CDN URL the runtime loads. */
export interface JsLibraryOut {
  name: string;
  version: string;
  global: string;
  url: string;
  format: JsLibraryFormat;
  added_by?: string | null;
  added_at?: string | null;
}

export interface JsLibraryAdd {
  name: string;
  version: string;
  global: string;
  /** Optional — derived from name+version+format when omitted. */
  url?: string | null;
  format?: JsLibraryFormat;
}

export interface DatasetPreviewBody {
  params?: Record<string, unknown>;
  page?: number;
  page_size?: number;
}

// --- Scheduled rules (ADR-0021, the time layer) ----------------------------

export interface RuleAge {
  column: "created_at" | "updated_at";
  minutes: number;
}

export interface RuleNotifyAction {
  type: "notify";
  title: string;
  body?: string | null;
  roles?: string[];
  notify_owner?: boolean;
  notify_members?: boolean;
}

export type RuleAction = RuleNotifyAction;

export interface ScheduledRuleSpec {
  when?: string | null;
  age: RuleAge;
  action: RuleAction;
}

export interface ScheduledRuleOut {
  id: string;
  app_id: string;
  table_id: string;
  name: string;
  slug: string;
  description: string | null;
  enabled: boolean;
  config: ScheduledRuleSpec;
  created_by: string;
  created_at: string;
  updated_at: string | null;
}

export interface ScheduledRuleCreate {
  name: string;
  table_id: string;
  config: ScheduledRuleSpec;
  slug?: string;
  description?: string;
  enabled?: boolean;
}

export interface ScheduledRuleUpdate {
  name?: string;
  config?: ScheduledRuleSpec;
  description?: string;
  enabled?: boolean;
}

export interface ScheduledRulePreview {
  matched: number;
  would_fire: string[];
  would_clear: number;
}


// ---------------------------------------------------------------------------
// Interfaces (ADR-0025 / ADR-0028) — typed presentation surfaces over the app
// core. "web" is the implicit page/portal face; "voice" + "chat" are the two
// assistant surfaces sharing one brain (ADR-0028 Block E).
export type InterfaceKind = "web" | "voice" | "chat" | "rest" | "graphql";

export interface InterfaceOut {
  id: string;
  app_id: string;
  kind: InterfaceKind;
  name: string;
  config: Record<string, unknown>;
  enabled: boolean;
  /** ADR-0049 — has its own front-door surface/URL. */
  standalone: boolean;
  /** ADR-0049 — mountable as a widget inside another interface. */
  embedded: boolean;
  /** Per-app LLM config override (ADR-0033 Phase B); null ⇒ inherit the system agent. */
  llm_config_id: string | null;
  /** A custom agent driving this interface (ADR-0033 Phase C); null ⇒ system agent. */
  agent_id: string | null;
  /** A team driving this interface (ADR-0033 Phase F) — mutually exclusive with agent_id. */
  team_id: string | null;
  created_by: string;
  created_at: string;
  updated_at: string | null;
}
export interface InterfaceCreate {
  kind: InterfaceKind;
  name: string;
  config?: Record<string, unknown>;
  standalone?: boolean;
  embedded?: boolean;
  llm_config_id?: string | null;
  agent_id?: string | null;
  team_id?: string | null;
}
export interface InterfaceUpdate {
  name?: string;
  config?: Record<string, unknown>;
  enabled?: boolean;
  standalone?: boolean;
  embedded?: boolean;
  llm_config_id?: string | null;
  agent_id?: string | null;
  team_id?: string | null;
}

// Client
// ---------------------------------------------------------------------------

const base = "/apps";

export const appsApi = {
  // catalog
  dataTypes: () => api.get<DataTypeInfo[]>(`${base}/data-types`),

  // apps
  list: (params?: { app_id?: string }) =>
    api.get<AppSummary[]>(base + (params?.app_id ? `?app_id=${params.app_id}` : "")),
  create: (body: AppCreate) => api.post<AppSummary>(base, body),
  get: (appId: string) => api.get<AppDetail>(`${base}/${appId}`),
  update: (appId: string, body: AppUpdate) => api.patch<AppSummary>(`${base}/${appId}`, body),
  remove: (appId: string) => api.delete<void>(`${base}/${appId}`),
  /** Persist the ER schema-diagram node positions (admin) — whole-object replace. */
  saveErLayout: (appId: string, positions: Record<string, { x: number; y: number }>) =>
    api.put<void>(`${base}/${appId}/er-layout`, { positions }),

  // Button-runnable workflows (S14) — enabled app workflows whose active
  // version has a `button_clicked` trigger. Feeds the builder's run-workflow
  // action picker. NB: the builder must use THIS operator route, never the
  // portal `/apps/{slug}/workflows` twin (member-gated → silent 401 without
  // a portal session).
  listButtonWorkflows: (appId: string) =>
    api.get<{ id: string; name: string }[]>(`${base}/${appId}/button-workflows`),

  // forms (D11)
  listForms: (appId: string, params?: { table_id?: string }) =>
    api.get<FormOut[]>(
      `${base}/${appId}/forms` + (params?.table_id ? `?table_id=${params.table_id}` : ""),
    ),
  createForm: (appId: string, body: FormCreate) =>
    api.post<FormOut>(`${base}/${appId}/forms`, body),
  getForm: (appId: string, formId: string) =>
    api.get<FormOut>(`${base}/${appId}/forms/${formId}`),
  updateForm: (appId: string, formId: string, body: FormUpdate) =>
    api.patch<FormOut>(`${base}/${appId}/forms/${formId}`, body),
  deleteForm: (appId: string, formId: string) =>
    api.delete<void>(`${base}/${appId}/forms/${formId}`),

  // app Queries (ADR-0047) — the app-level server primitive / logic service layer
  listQueries: (appId: string) => api.get<AppQueryOut[]>(`${base}/${appId}/queries`),
  createQuery: (appId: string, body: AppQueryUpsert) =>
    api.post<AppQueryOut>(`${base}/${appId}/queries`, body),
  getQuery: (appId: string, queryId: string) =>
    api.get<AppQueryOut>(`${base}/${appId}/queries/${queryId}`),
  updateQuery: (appId: string, queryId: string, body: AppQueryUpsert) =>
    api.put<AppQueryOut>(`${base}/${appId}/queries/${queryId}`, body),
  deleteQuery: (appId: string, queryId: string) =>
    api.delete<void>(`${base}/${appId}/queries/${queryId}`),
  /** Builder test-run (operator, unrestricted) — a `logic` Query really writes. */
  runQuery: (appId: string, queryId: string, input?: Record<string, unknown>) =>
    api.post<{ result: unknown }>(`${base}/${appId}/queries/${queryId}/run`, { input: input ?? {} }),
  /** The app's live OpenAPI doc — generated from the REST interface (ADR-0051). */
  getOpenapi: (appId: string) => api.get<OpenApiDoc>(`${base}/${appId}/openapi.json`),
  submitForm: (appId: string, formId: string, data: Row) =>
    api.post<FormSubmitResult>(`${base}/${appId}/forms/${formId}/submit`, { data }),
  uploadAttachment: (appId: string, formId: string, file: File) => {
    const fd = new FormData();
    fd.append("file", file);
    return api.upload<AttachmentOut>(`${base}/${appId}/forms/${formId}/attachments`, fd);
  },
  /** Form-less attachment upload (S2) — for a `file` column edited from the
   *  data grid / record view (not tied to a form). */
  uploadAppAttachment: (appId: string, file: File) => {
    const fd = new FormData();
    fd.append("file", file);
    return api.upload<AttachmentOut>(`${base}/${appId}/attachments`, fd);
  },
  /** Fetch a `file`-column attachment (authenticated) and trigger a
   *  client-side download with its filename. */
  downloadAttachment: async (appId: string, ref: string, filename: string) => {
    const { useAuthStore } = await import("./auth");
    const { triggerBlobDownload } = await import("./api-workflows");
    const token = useAuthStore.getState().token;
    const res = await fetch(
      `/api/admin/apps/${appId}/attachments?ref=${encodeURIComponent(ref)}`,
      { headers: token ? { Authorization: `Bearer ${token}` } : {} },
    );
    if (!res.ok) throw new Error(`Download failed: ${res.status} ${res.statusText}`);
    triggerBlobDownload(await res.blob(), filename || "attachment");
  },

  // pages (P7 — interface layer)
  pageCatalog: () => api.get<PageComponentSpec[]>(`${base}/page-catalog`),
  listPages: (appId: string) => api.get<PageOut[]>(`${base}/${appId}/pages`),
  createPage: (appId: string, body: PageCreate) =>
    api.post<PageOut>(`${base}/${appId}/pages`, body),
  getPage: (appId: string, pageId: string) =>
    api.get<PageOut>(`${base}/${appId}/pages/${pageId}`),
  updatePage: (appId: string, pageId: string, body: PageUpdate) =>
    api.patch<PageOut>(`${base}/${appId}/pages/${pageId}`, body),
  deletePage: (appId: string, pageId: string) =>
    api.delete<void>(`${base}/${appId}/pages/${pageId}`),

  // roles & members (app RBAC usage plane)
  listRoles: (appId: string) => api.get<RoleOut[]>(`${base}/${appId}/roles`),
  createRole: (appId: string, body: RoleCreate) =>
    api.post<RoleOut>(`${base}/${appId}/roles`, body),
  updateRole: (appId: string, roleId: string, body: RoleUpdate) =>
    api.patch<RoleOut>(`${base}/${appId}/roles/${roleId}`, body),
  deleteRole: (appId: string, roleId: string) =>
    api.delete<void>(`${base}/${appId}/roles/${roleId}`),
  // ADR-0059 — external resources the app CONSUMES (Core "Dataset" / "Knowledge
  // base" areas). Per-role visibility (assistant_scope) is scoped to this set.
  listLinkedDatasets: (appId: string) =>
    api.get<LinkedResource[]>(`${base}/${appId}/linked-datasets`),
  linkDataset: (appId: string, datasetId: string) =>
    api.post<LinkedResource[]>(`${base}/${appId}/linked-datasets`, { id: datasetId }),
  unlinkDataset: (appId: string, datasetId: string) =>
    api.delete<void>(`${base}/${appId}/linked-datasets/${datasetId}`),
  listLinkedKbs: (appId: string) =>
    api.get<LinkedResource[]>(`${base}/${appId}/linked-knowledge-bases`),
  linkKb: (appId: string, kbId: string) =>
    api.post<LinkedResource[]>(`${base}/${appId}/linked-knowledge-bases`, { id: kbId }),
  unlinkKb: (appId: string, kbId: string) =>
    api.delete<void>(`${base}/${appId}/linked-knowledge-bases/${kbId}`),
  // ADR-0060 S5 — tenant-global Report Types the app consumes (Core "Report").
  listLinkedReportTypes: (appId: string) =>
    api.get<LinkedResource[]>(`${base}/${appId}/linked-report-types`),
  linkReportType: (appId: string, typeId: string) =>
    api.post<LinkedResource[]>(`${base}/${appId}/linked-report-types`, { id: typeId }),
  unlinkReportType: (appId: string, typeId: string) =>
    api.delete<void>(`${base}/${appId}/linked-report-types/${typeId}`),
  listMembers: (appId: string) => api.get<MemberOut[]>(`${base}/${appId}/members`),
  addMember: (appId: string, body: MemberCreate) =>
    api.post<MemberOut>(`${base}/${appId}/members`, body),
  updateMemberRole: (appId: string, memberId: string, roleId: string) =>
    api.patch<MemberOut>(`${base}/${appId}/members/${memberId}`, { role_id: roleId }),
  removeMember: (appId: string, memberId: string) =>
    api.delete<void>(`${base}/${appId}/members/${memberId}`),
  /** Set a member's per-app profile attribute values (operator surface). */
  updateMemberAttrs: (appId: string, memberId: string, attrs: Record<string, unknown>) =>
    api.patch<MemberOut>(`${base}/${appId}/members/${memberId}/attrs`, { attrs }),

  // per-app user profile — member-attribute schema (access-model)
  listProfileFields: (appId: string) =>
    api.get<ProfileField[]>(`${base}/${appId}/profile-fields`),
  createProfileField: (appId: string, body: ProfileFieldCreate) =>
    api.post<ProfileField>(`${base}/${appId}/profile-fields`, body),
  updateProfileField: (appId: string, fieldId: string, body: ProfileFieldUpdate) =>
    api.patch<ProfileField>(`${base}/${appId}/profile-fields/${fieldId}`, body),
  deleteProfileField: (appId: string, fieldId: string) =>
    api.delete<void>(`${base}/${appId}/profile-fields/${fieldId}`),

  // row-level policies (RLS) — per (table, role)
  listPolicies: (appId: string, tableId: string) =>
    api.get<PolicyOut[]>(`${base}/${appId}/tables/${tableId}/policies`),
  upsertPolicy: (appId: string, tableId: string, body: PolicyUpsert) =>
    api.put<PolicyOut>(`${base}/${appId}/tables/${tableId}/policies`, body),
  deletePolicy: (appId: string, tableId: string, policyId: string) =>
    api.delete<void>(`${base}/${appId}/tables/${tableId}/policies/${policyId}`),

  // tables
  listTables: (appId: string) => api.get<TableSummary[]>(`${base}/${appId}/tables`),
  createTable: (appId: string, body: TableCreate) =>
    api.post<TableDetail>(`${base}/${appId}/tables`, body),
  getTable: (appId: string, tableId: string) =>
    api.get<TableDetail>(`${base}/${appId}/tables/${tableId}`),
  updateTable: (appId: string, tableId: string, body: TableUpdate) =>
    api.patch<TableDetail>(`${base}/${appId}/tables/${tableId}`, body),
  removeTable: (appId: string, tableId: string) =>
    api.delete<void>(`${base}/${appId}/tables/${tableId}`),
  /** Relation-picker options for a target table: `[{id, label}]`. */
  tableOptions: (appId: string, tableId: string, search?: string) =>
    api.get<RelationOption[]>(
      `${base}/${appId}/tables/${tableId}/options${search ? `?search=${encodeURIComponent(search)}` : ""}`,
    ),

  // columns
  addColumn: (appId: string, tableId: string, body: ColumnIn) =>
    api.post<TableDetail>(`${base}/${appId}/tables/${tableId}/columns`, body),
  renameColumn: (appId: string, tableId: string, columnId: string, name: string) =>
    api.patch<TableDetail>(`${base}/${appId}/tables/${tableId}/columns/${columnId}`, { name }),
  dropColumn: (appId: string, tableId: string, columnId: string) =>
    api.delete<void>(`${base}/${appId}/tables/${tableId}/columns/${columnId}`),

  // views (saved Grid presentations)
  listViews: (appId: string, tableId: string) =>
    api.get<ViewOut[]>(`${base}/${appId}/tables/${tableId}/views`),
  createView: (appId: string, tableId: string, body: ViewCreate) =>
    api.post<ViewOut>(`${base}/${appId}/tables/${tableId}/views`, body),
  updateView: (appId: string, tableId: string, viewId: string, body: ViewUpdate) =>
    api.patch<ViewOut>(`${base}/${appId}/tables/${tableId}/views/${viewId}`, body),
  deleteView: (appId: string, tableId: string, viewId: string) =>
    api.delete<void>(`${base}/${appId}/tables/${tableId}/views/${viewId}`),

  // unique sets
  createUniqueSet: (appId: string, tableId: string, body: UniqueSetIn) =>
    api.post<TableDetail>(`${base}/${appId}/tables/${tableId}/unique-sets`, body),
  deleteUniqueSet: (appId: string, tableId: string, setId: string) =>
    api.delete<void>(`${base}/${appId}/tables/${tableId}/unique-sets/${setId}`),

  // promote to prod (ADR-0010 P5.5)
  promote: (appId: string) =>
    api.post<PromoteResult>(`${base}/${appId}/promote`, {}),

  // datasets (ADR-0019 S5a.3) — read-model authoring + live preview
  listDatasets: (appId: string) => api.get<DatasetOut[]>(`${base}/${appId}/datasets`),
  createDataset: (appId: string, body: DatasetCreate) =>
    api.post<DatasetOut>(`${base}/${appId}/datasets`, body),
  getDataset: (appId: string, datasetId: string) =>
    api.get<DatasetOut>(`${base}/${appId}/datasets/${datasetId}`),
  updateDataset: (appId: string, datasetId: string, body: DatasetUpdate) =>
    api.patch<DatasetOut>(`${base}/${appId}/datasets/${datasetId}`, body),
  deleteDataset: (appId: string, datasetId: string) =>
    api.delete<void>(`${base}/${appId}/datasets/${datasetId}`),
  previewDataset: (appId: string, datasetId: string, body: DatasetPreviewBody = {}) =>
    api.post<RowsPage>(`${base}/${appId}/datasets/${datasetId}/preview`, body),

  // interfaces (ADR-0025) — typed presentation surfaces (web/voice/…) over the
  // same app core. One per kind per app.
  listInterfaces: (appId: string) => api.get<InterfaceOut[]>(`${base}/${appId}/interfaces`),
  createInterface: (appId: string, body: InterfaceCreate) =>
    api.post<InterfaceOut>(`${base}/${appId}/interfaces`, body),
  updateInterface: (appId: string, interfaceId: string, body: InterfaceUpdate) =>
    api.patch<InterfaceOut>(`${base}/${appId}/interfaces/${interfaceId}`, body),
  deleteInterface: (appId: string, interfaceId: string) =>
    api.delete<void>(`${base}/${appId}/interfaces/${interfaceId}`),
  /** AI bootstrap for the chat empty-state suggestions (Wave-1): drafts natural starter
   *  questions from the app schema. Nothing saved — the operator keeps each into
   *  `config.starters`. 422 if no LLM is configured. */
  proposeAssistantStarters: (appId: string) =>
    api.post<{ starters: string[] }>(`${base}/${appId}/assistant-starters/propose`, {}),

  // custom-component library (ADR-0022 #2) — reusable React components, live-
  // referenced by `custom_component` page nodes (source="library").
  listCustomComponents: (appId: string) =>
    api.get<CustomComponentOut[]>(`${base}/${appId}/custom-components`),
  createCustomComponent: (appId: string, body: CustomComponentCreate) =>
    api.post<CustomComponentOut>(`${base}/${appId}/custom-components`, body),
  getCustomComponent: (appId: string, componentId: string) =>
    api.get<CustomComponentOut>(`${base}/${appId}/custom-components/${componentId}`),
  updateCustomComponent: (appId: string, componentId: string, body: CustomComponentUpdate) =>
    api.patch<CustomComponentOut>(`${base}/${appId}/custom-components/${componentId}`, body),
  deleteCustomComponent: (appId: string, componentId: string) =>
    api.delete<void>(`${base}/${appId}/custom-components/${componentId}`),

  // external JS libraries (ADR-0046) — per-app, runtime-CDN pointers. The
  // backend stores metadata only; add/remove return the updated list. `name`
  // is path-encoded on delete (it may contain `@scope/pkg`).
  listJsLibraries: (appId: string) =>
    api.get<JsLibraryOut[]>(`${base}/${appId}/js-libraries`),
  addJsLibrary: (appId: string, body: JsLibraryAdd) =>
    api.post<JsLibraryOut[]>(`${base}/${appId}/js-libraries`, body),
  removeJsLibrary: (appId: string, name: string) =>
    api.delete<void>(`${base}/${appId}/js-libraries/${name.split("/").map(encodeURIComponent).join("/")}`),

  // scheduled rules (ADR-0021) — time-driven rules (when + age → notify /
  // transition), edge-triggered. `preview` dry-runs against the live table.
  listScheduledRules: (appId: string) =>
    api.get<ScheduledRuleOut[]>(`${base}/${appId}/scheduled-rules`),
  createScheduledRule: (appId: string, body: ScheduledRuleCreate) =>
    api.post<ScheduledRuleOut>(`${base}/${appId}/scheduled-rules`, body),
  getScheduledRule: (appId: string, ruleId: string) =>
    api.get<ScheduledRuleOut>(`${base}/${appId}/scheduled-rules/${ruleId}`),
  updateScheduledRule: (appId: string, ruleId: string, body: ScheduledRuleUpdate) =>
    api.patch<ScheduledRuleOut>(`${base}/${appId}/scheduled-rules/${ruleId}`, body),
  deleteScheduledRule: (appId: string, ruleId: string) =>
    api.delete<void>(`${base}/${appId}/scheduled-rules/${ruleId}`),
  previewScheduledRule: (appId: string, ruleId: string) =>
    api.post<ScheduledRulePreview>(`${base}/${appId}/scheduled-rules/${ruleId}/preview`, {}),

  // rows (Data API) — ``env`` defaults to dev; pass "prod" to target the
  // promoted environment (ADR-0010 P5.5).
  queryRows: (appId: string, tableId: string, body: RowQuery, env?: AppEnv) =>
    api.post<RowsPage>(
      `${base}/${appId}/tables/${tableId}/rows/query${envQuery(env)}`,
      body,
    ),
  // Server-side aggregation (GROUP BY + count/sum/avg/min/max) over the WHOLE
  // dataset — powers the page builder's aggregate-query preview (R2b).
  aggregate: (appId: string, tableId: string, body: AggregateBody, env?: AppEnv) =>
    api.post<AggregateResult>(
      `${base}/${appId}/tables/${tableId}/aggregate${envQuery(env)}`,
      body,
    ),
  // Server-side resolve of ONE resource for the builder live-preview — the only
  // path for http/js/static (table/dataset preview client-side). The
  // client renders the http `{{ }}` params (pageExpr) and posts CONCRETE values;
  // `url` is origin-locked server-side. Returns rows for the grid + raw `data`.
  previewResource: (
    appId: string,
    body: {
      spec: PageQuery;
      url?: string;
      headers?: Record<string, unknown>;
      query?: Record<string, unknown>;
      body?: unknown;
      filters?: { column: string; op: string; value: unknown }[];
    },
  ) =>
    api.post<{ rows: Row[]; total: number; data: unknown }>(
      `${base}/${appId}/resources/preview`,
      body,
    ),
  createRow: (appId: string, tableId: string, data: Row, env?: AppEnv) =>
    api.post<Row>(`${base}/${appId}/tables/${tableId}/rows${envQuery(env)}`, data),
  getRow: (appId: string, tableId: string, rowId: string, env?: AppEnv) =>
    api.get<Row>(`${base}/${appId}/tables/${tableId}/rows/${rowId}${envQuery(env)}`),
  updateRow: (appId: string, tableId: string, rowId: string, data: Row, env?: AppEnv) =>
    api.patch<Row>(
      `${base}/${appId}/tables/${tableId}/rows/${rowId}${envQuery(env)}`,
      data,
    ),
  deleteRow: (appId: string, tableId: string, rowId: string, env?: AppEnv) =>
    api.delete<void>(
      `${base}/${appId}/tables/${tableId}/rows/${rowId}${envQuery(env)}`,
    ),
  upsertRow: (appId: string, tableId: string, uniqueSet: string, data: Row) =>
    api.post<Row>(
      `${base}/${appId}/tables/${tableId}/rows/upsert?unique_set=${encodeURIComponent(uniqueSet)}`,
      data,
    ),
};
